/**
 * Chart V2 Utility Functions
 *
 * Normalize Chart V2 data for the shared Victory Native renderers.
 * Calculate chart dimensions and values
 *
 * UPDATED: Now supports multi-series charts
 */

import type {
  T_ChartData,
  T_ChartDataPoint,
  T_ChartValueFormat,
} from "@lattice/core-lib/types/chart";
import { getSeriesNames, getSeriesValue } from "@lattice/core-lib/types/chart";

/**
 * Recharts and Victory Native use different names for the same monotone-X
 * interpolation. Keep the platform adapters aligned through this policy.
 */
export const CHART_LINE_CURVE_TYPES = {
  native: "monotoneX",
  web: "monotone",
} as const;

export interface T_GiftedChartDataPoint {
  value: number;
  label: string;
  frontColor?: string;
}

export interface T_GiftedMultiSeriesData {
  data: T_GiftedChartDataPoint[];
  color: string;
}

// =============================================================================
// Y-AXIS & STATISTICS HELPERS
// =============================================================================

/**
 * Smart Y-axis range calculation result
 */
export type T_SmartYAxisResult = {
  yMin: number;
  yMax: number;
  hasNegatives: boolean;
  hasPositives: boolean;
  hasMixedSigns: boolean;
};

/**
 * Calculate percentile value from sorted array
 */
export function getPercentile(
  sortedValues: number[],
  percentile: number,
): number {
  if (sortedValues.length === 0) return 0;
  if (sortedValues.length === 1) return sortedValues[0] ?? 0;

  const index = (percentile / 100) * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  const lowerVal = sortedValues[lower] ?? 0;
  const upperVal = sortedValues[upper] ?? 0;

  return lowerVal + weight * (upperVal - lowerVal);
}

/**
 * Calculate smart Y-axis range
 *
 * Strategy: When all values are positive, start from 0. When negatives exist,
 * extend the axis below zero so the line renders correctly. Uses percentile-based
 * outlier detection on the positive side to prevent absurdly tall charts.
 *
 * Features:
 * - yMin is 0 for all-positive data; negative for data with negatives
 * - Detects outliers using IQR-based analysis for the max
 * - Uses 95th percentile for max when outliers exist
 */
export function calculateSmartYAxisRange(
  allValues: number[],
): T_SmartYAxisResult {
  // Handle edge cases
  if (allValues.length === 0) {
    return {
      yMin: 0,
      yMax: 100,
      hasNegatives: false,
      hasPositives: false,
      hasMixedSigns: false,
    };
  }

  // Sort values for percentile calculations
  const sorted = [...allValues].sort((a, b) => a - b);
  const rawMin = sorted[0] ?? 0;
  const rawMax = sorted[sorted.length - 1] ?? 0;

  // Determine sign characteristics
  const hasNegatives = rawMin < 0;
  const hasPositives = rawMax > 0;
  const hasMixedSigns = hasNegatives && hasPositives;

  // For yMax, use outlier detection on positive values
  const positiveValues = sorted.filter((v) => v > 0);
  const negativeValues = sorted.filter((v) => v < 0);

  // All-negative data: axis spans from rawMin (with padding) up to 0
  if (positiveValues.length === 0) {
    const absMin = Math.abs(rawMin) || 100;
    return {
      yMin: -(absMin * 1.15),
      yMax: 0,
      hasNegatives,
      hasPositives,
      hasMixedSigns,
    };
  }

  // yMin: 0 for all-positive data; negative (with padding) when negatives exist
  let yMin = 0;
  if (hasNegatives && negativeValues.length > 0) {
    const mostNegative = negativeValues[0] ?? 0; // already sorted ascending
    yMin = mostNegative * 1.15; // extend 15% below the most-negative value
  }

  const posMax = positiveValues[positiveValues.length - 1] ?? 0;

  // Calculate percentiles for outlier detection (only on positive values)
  const p75 = getPercentile(positiveValues, 75);
  const p95 = getPercentile(positiveValues, 95);
  const p25 = getPercentile(positiveValues, 25);

  // IQR-based outlier detection
  const iqr = p75 - p25;
  const upperFence = p75 + 1.5 * iqr;
  const hasUpperOutliers = posMax > upperFence;

  let yMax: number;

  if (hasUpperOutliers && positiveValues.length > 4) {
    // Use 95th percentile + padding when there are outliers
    yMax = p95 + Math.abs(p95) * 0.15;
  } else {
    // No outliers: use raw max with 10% padding
    yMax = posMax + Math.abs(posMax) * 0.1;
  }

  // Ensure yMax is at least some minimum value
  if (yMax <= 0) {
    yMax = 100;
  }

  return {
    yMin,
    yMax,
    hasNegatives,
    hasPositives,
    hasMixedSigns,
  };
}

/**
 * Generate nice Y-axis anchors based on gridline count.
 * Spans from yMin (may be negative) to yMax.
 */
export function generateYAxisAnchors(
  yMin: number,
  yMax: number,
  _hasMixedSigns: boolean,
  count: 2 | 3 = 3,
): number[] {
  if (count === 2) {
    return [yMin, yMax];
  }

  const mid = (yMin + yMax) / 2;
  return [yMin, mid, yMax];
}

// =============================================================================
// NUMBER FORMATTING
// =============================================================================

/**
 * Format compact number for axis labels
 * Always outputs clean, unit-suffixed values: 0, 250B, 1.5T, 120M, 45K
 */
export function formatCompactNumber(value: number): string {
  if (value === 0) return "0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1e12) {
    const formatted = absValue / 1e12;
    return `${sign}${formatted % 1 === 0 ? formatted.toFixed(0) : formatted.toFixed(1)}T`;
  }
  if (absValue >= 1e9) {
    const formatted = absValue / 1e9;
    return `${sign}${formatted % 1 === 0 ? formatted.toFixed(0) : formatted.toFixed(1)}B`;
  }
  if (absValue >= 1e6) {
    const formatted = absValue / 1e6;
    return `${sign}${formatted % 1 === 0 ? formatted.toFixed(0) : formatted.toFixed(1)}M`;
  }
  if (absValue >= 1e3) {
    const formatted = absValue / 1e3;
    return `${sign}${formatted % 1 === 0 ? formatted.toFixed(0) : formatted.toFixed(1)}K`;
  }
  if (absValue >= 1) {
    return `${sign}${absValue % 1 === 0 ? absValue.toFixed(0) : absValue.toFixed(1)}`;
  }
  return `${sign}${absValue.toFixed(2)}`;
}

/**
 * Truncate label for display
 */
export function truncateLabel(label: string, maxLength: number = 8): string {
  if (label.length <= maxLength) return label;
  return `${label.substring(0, maxLength - 1)}...`;
}

/**
 * Format exact value with locale comma separators
 */
export function formatExactValue(value: number): string {
  return value.toLocaleString();
}

// =============================================================================
// DATE FORMATTING & DETECTION
// =============================================================================

/**
 * Month name patterns for date detection
 */
export const MONTH_PATTERNS = [
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  /^(january|february|march|april|may|june|july|august|september|october|november|december)/i,
];

/**
 * Month abbreviation mapping
 */
const MONTH_TO_ABBREV: Record<string, string> = {
  january: "Jan",
  february: "Feb",
  march: "Mar",
  april: "Apr",
  may: "May",
  june: "Jun",
  july: "Jul",
  august: "Aug",
  september: "Sep",
  october: "Oct",
  november: "Nov",
  december: "Dec",
  jan: "Jan",
  feb: "Feb",
  mar: "Mar",
  apr: "Apr",
  jun: "Jun",
  jul: "Jul",
  aug: "Aug",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dec: "Dec",
};

const MONTH_NUM_TO_ABBREV = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function normalizeChartLabel(label: unknown): string {
  if (typeof label === "string") return label;
  if (typeof label === "number" || typeof label === "boolean") {
    return String(label);
  }
  if (label instanceof Date) return label.toISOString();
  return "";
}

/**
 * Detect if a label looks like a date and format it smartly
 * Returns multiline format for multi-year charts: "Jan\n2016"
 * Returns single line for same-year charts: "Dec 1"
 *
 * Examples (multiYear=true):
 * - "December 1, 2025" -> "Dec\n2025"
 * - "January 2016" -> "Jan\n2016"
 * - "2025-12-01" -> "Dec\n2025"
 *
 * Examples (multiYear=false):
 * - "December 1, 2025" -> "Dec 1"
 * - "Dec 15" -> "Dec 15"
 */
export function formatDateLabel(
  label: unknown,
  multiYear: boolean = false,
): string {
  const trimmed = normalizeChartLabel(label).trim();

  // Pattern: "December 1, 2025" or "December 1" or "Dec 1" or "January 2016" (month + day/year)
  const monthDayYearMatch = trimmed.match(
    /^([a-zA-Z]+)\s+(\d{1,2})(?:[,\s]+(\d{4}))?$/,
  );
  if (monthDayYearMatch) {
    const monthStr = monthDayYearMatch[1];
    const secondNum = monthDayYearMatch[2]; // Could be day or year
    const thirdNum = monthDayYearMatch[3]; // Year if present
    if (monthStr === undefined || secondNum === undefined) {
      throw new Error("Expected month and number in date match");
    }
    const monthLower = monthStr.toLowerCase();
    const monthAbbrev = MONTH_TO_ABBREV[monthLower] ?? monthStr.slice(0, 3);

    // If third number exists, it's the year (format: "December 1, 2025")
    if (thirdNum !== undefined) {
      if (multiYear) {
        return `${monthAbbrev}\n${thirdNum}`;
      }
      const day = parseInt(secondNum, 10);
      return `${monthAbbrev} ${day}`;
    }

    // Check if second number is a year (4 digits) or day (1-2 digits)
    const numValue = parseInt(secondNum, 10);
    if (secondNum.length === 4 || numValue > 31) {
      // It's a year (e.g., "January 2016")
      if (multiYear) {
        return `${monthAbbrev}\n${secondNum}`;
      }
      return `${monthAbbrev} ${secondNum}`;
    }

    // It's a day
    if (multiYear) {
      return monthAbbrev; // Just show month for multiYear without year info
    }
    return `${monthAbbrev} ${numValue}`;
  }

  // Pattern: "1 December 2025" or "1 Dec"
  const dayMonthMatch = trimmed.match(
    /^(\d{1,2})\s+([a-zA-Z]+)(?:\s+(\d{4}))?$/,
  );
  if (dayMonthMatch) {
    const dayStr = dayMonthMatch[1];
    const monthStr = dayMonthMatch[2];
    const yearStr = dayMonthMatch[3];
    if (dayStr === undefined || monthStr === undefined) {
      throw new Error("Expected day and month in date match");
    }
    const day = parseInt(dayStr, 10);
    const monthLower = monthStr.toLowerCase();
    const monthAbbrev = MONTH_TO_ABBREV[monthLower] ?? monthStr.slice(0, 3);

    if (multiYear && yearStr !== undefined) {
      return `${monthAbbrev}\n${yearStr}`;
    }
    return `${monthAbbrev} ${day}`;
  }

  // Pattern: "2025-12-01" (ISO format)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const yearStr = isoMatch[1];
    const monthStr = isoMatch[2];
    const dayStr = isoMatch[3];
    if (
      yearStr === undefined ||
      monthStr === undefined ||
      dayStr === undefined
    ) {
      throw new Error("Expected year, month and day in ISO date match");
    }
    const monthNum = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);
    const monthAbbrev = MONTH_NUM_TO_ABBREV[monthNum] ?? "Mon";

    if (multiYear) {
      return `${monthAbbrev}\n${yearStr}`;
    }
    return `${monthAbbrev} ${day}`;
  }

  // Pattern: "12/15/2025" or "12/15" (US format - month/day)
  const usDateMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (usDateMatch) {
    const monthStr = usDateMatch[1];
    const dayStr = usDateMatch[2];
    const yearStr = usDateMatch[3];
    if (monthStr === undefined || dayStr === undefined) {
      throw new Error("Expected month and day in US date match");
    }
    const monthNum = parseInt(monthStr, 10) - 1;
    const day = parseInt(dayStr, 10);
    const monthAbbrev = MONTH_NUM_TO_ABBREV[monthNum] ?? "Mon";

    if (multiYear && yearStr !== undefined) {
      // Handle 2-digit year
      const fullYear = yearStr.length === 2 ? `20${yearStr}` : yearStr;
      return `${monthAbbrev}\n${fullYear}`;
    }
    return `${monthAbbrev} ${day}`;
  }

  return trimmed;
}

/**
 * Check if labels appear to be dates
 */
export function labelsAreDates(labels: readonly unknown[]): boolean {
  if (labels.length === 0) return false;

  // Check first few labels for date patterns
  const samplesToCheck = Math.min(3, labels.length);
  let dateCount = 0;
  let validSampleCount = 0;

  for (let i = 0; i < samplesToCheck; i++) {
    const label = normalizeChartLabel(labels[i]).trim().toLowerCase();
    if (label.length === 0 || label === "undefined") continue;
    validSampleCount++;

    const isDate =
      MONTH_PATTERNS.some((p) => p.test(label)) ||
      /^\d{4}-\d{2}-\d{2}$/.test(label) ||
      /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(label) ||
      /^\d{1,2}\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(
        label,
      );

    if (isDate) dateCount++;
  }

  return validSampleCount > 0 && dateCount >= Math.ceil(validSampleCount / 2);
}

/**
 * Extract year from a date label string
 * Returns null if no year can be extracted
 */
export function extractYearFromLabel(label: unknown): number | null {
  const trimmed = normalizeChartLabel(label).trim();

  // Pattern: "January 2016" or "December 1, 2025"
  const yearMatch = trimmed.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) {
    return parseInt(yearMatch[0], 10);
  }

  // Pattern: "2025-12-01" (ISO format)
  const isoMatch = trimmed.match(/^(\d{4})-\d{2}-\d{2}$/);
  if (isoMatch?.[1]) {
    return parseInt(isoMatch[1], 10);
  }

  // Pattern: "12/15/2025" or "12/15/25" (US format with year)
  const usDateMatch = trimmed.match(/^\d{1,2}\/\d{1,2}\/(\d{2,4})$/);
  if (usDateMatch?.[1]) {
    const yearStr = usDateMatch[1];
    if (yearStr.length === 2) {
      return parseInt(`20${yearStr}`, 10);
    }
    return parseInt(yearStr, 10);
  }

  return null;
}

/**
 * Check if labels span multiple years
 */
export function labelsSpanMultipleYears(labels: readonly unknown[]): boolean {
  const years = new Set<number>();

  for (const label of labels) {
    const year = extractYearFromLabel(label);
    if (year !== null) {
      years.add(year);
      if (years.size > 1) {
        return true;
      }
    }
  }

  return false;
}

// =============================================================================
// DATA ANALYSIS
// =============================================================================

/**
 * Find indices of minimum and maximum values in the data
 */
export function findMinMaxIndices(data: T_ChartData): {
  minIndex: number;
  maxIndex: number;
} {
  const seriesNames = getSeriesNames(data);
  let minValue = Infinity;
  let maxValue = -Infinity;
  let minIndex = 0;
  let maxIndex = 0;

  data.data.forEach((point, index) => {
    for (const name of seriesNames) {
      const value = getSeriesValue(point, name);
      if (value !== null) {
        if (value < minValue) {
          minValue = value;
          minIndex = index;
        }
        if (value > maxValue) {
          maxValue = value;
          maxIndex = index;
        }
      }
    }
  });

  return { minIndex, maxIndex };
}

/**
 * Collect all values from chart data for analysis
 */
export function collectAllValues(chartData: T_ChartData): number[] {
  const seriesNames = getSeriesNames(chartData);
  const allValues: number[] = [];

  for (const point of chartData.data) {
    for (const name of seriesNames) {
      const value = getSeriesValue(point, name);
      if (value !== null) {
        allValues.push(value);
      }
    }
  }

  return allValues;
}

// Chart colors for multi-series (matching rails-frontend CHART_COLORS)
export const CHART_COLORS = [
  "#3b82f6", // blue
  "#22c55e", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#84cc16", // lime
];

/**
 * Transform Chart V2 data points to Gifted Charts format (single series)
 *
 * @param data - Chart V2 data
 * @param color - Optional color for all bars/points
 * @returns Array of Gifted Charts data points
 */
export function transformToGiftedFormat(
  data: T_ChartDataPoint[],
  color?: string,
): T_GiftedChartDataPoint[] {
  return data.map((point) => {
    // Handle both single-series and multi-series formats
    const value =
      "value" in point && typeof point.value === "number" ? point.value : 0;
    const label = typeof point.label === "string" ? point.label : "";

    return {
      value,
      label,
      ...(color && { frontColor: color }),
    };
  });
}

/**
 * Transform multi-series Chart V2 data to Gifted Charts format
 * Returns array of data sets for multi-line charts
 *
 * @param chartData - Full chart data with series info
 * @param colors - Array of colors for each series
 * @returns Array of data sets, one per series
 */
export function transformToMultiSeriesFormat(
  chartData: T_ChartData,
  colors: string[] = CHART_COLORS,
): T_GiftedMultiSeriesData[] {
  const seriesNames = getSeriesNames(chartData);

  return seriesNames.map((seriesName, index) => {
    const colorIndex = index % colors.length;
    const seriesColor = colors[colorIndex];
    if (seriesColor === undefined) {
      throw new Error(`Expected color at index ${colorIndex}`);
    }

    const data: T_GiftedChartDataPoint[] = chartData.data.map((point) => {
      const value = getSeriesValue(point, seriesName);
      const label = typeof point.label === "string" ? point.label : "";

      return {
        value: value ?? 0, // Replace null with 0 for rendering
        label,
        frontColor: seriesColor,
      };
    });

    return {
      data,
      color: seriesColor,
    };
  });
}

/**
 * Calculate minimum value for chart Y-axis (supports multi-series)
 *
 * @param chartData - Full chart data
 * @returns Minimum value
 */
export function calculateMinValue(
  chartData: T_ChartData | T_ChartDataPoint[],
): number {
  // Handle both old format (array) and new format (full chartData)
  if (Array.isArray(chartData)) {
    if (chartData.length === 0) return 0;
    const values = chartData
      .map((d) => ("value" in d && typeof d.value === "number" ? d.value : 0))
      .filter((v) => v !== 0);
    if (values.length === 0) return 0;
    const min = Math.min(...values);
    return min >= 0 ? 0 : Math.floor(min * 1.1);
  }

  // New: full chart data with possible multi-series
  const seriesNames = getSeriesNames(chartData);
  const allValues: number[] = [];

  for (const point of chartData.data) {
    for (const name of seriesNames) {
      const value = getSeriesValue(point, name);
      if (value !== null) {
        allValues.push(value);
      }
    }
  }

  if (allValues.length === 0) return 0;
  const min = Math.min(...allValues);
  return min >= 0 ? 0 : Math.floor(min * 1.1);
}

function trimFormattedNumber(value: string): string {
  let formatted = value;
  while (formatted.includes(".") && formatted.endsWith("0")) {
    formatted = formatted.slice(0, -1);
  }
  if (formatted.endsWith(".")) {
    formatted = formatted.slice(0, -1);
  }
  return formatted === "-0" ? "0" : formatted;
}

/**
 * Format large numbers for display
 * 1000 -> 1K, 1000000 -> 1M
 *
 * @param value - Number to format
 * @returns Formatted string
 */
export function formatChartValue(value: number): string {
  if (Math.abs(value) < 0.0005) return "0";
  if (Math.abs(value) >= 1000000) {
    return `${trimFormattedNumber((value / 1000000).toFixed(1))}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `${trimFormattedNumber((value / 1000).toFixed(1))}K`;
  }
  if (Math.abs(value) >= 100) {
    return trimFormattedNumber(value.toFixed(0));
  }
  if (Math.abs(value) >= 10) {
    return trimFormattedNumber(value.toFixed(1));
  }
  if (Math.abs(value) >= 1) {
    return trimFormattedNumber(value.toFixed(2));
  }
  if (Math.abs(value) >= 0.01) {
    return trimFormattedNumber(value.toFixed(2));
  }
  return trimFormattedNumber(value.toFixed(3));
}

/**
 * Higher-precision variant for the touch-and-hold scan readout. Axis labels
 * stay compact (~3 significant figures via formatChartValue), but while
 * scanning the user is inspecting exact points, so keep two decimals at every
 * magnitude instead of collapsing adjacent values (371.83 and 372.45 both
 * render as "372" through the axis formatter).
 */
export function formatChartDetailValue(value: number): string {
  if (Math.abs(value) < 0.0005) return "0";
  if (Math.abs(value) >= 1000000) {
    return `${trimFormattedNumber((value / 1000000).toFixed(2))}M`;
  }
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
  }
  if (Math.abs(value) >= 0.01) {
    return trimFormattedNumber(value.toFixed(2));
  }
  return trimFormattedNumber(value.toFixed(4));
}

export type T_ChartValueDisplayOptions = {
  valueFormat?: T_ChartValueFormat;
  valuePrefix?: string;
  valueSuffix?: string;
};

function getDefaultChartValuePrefix(
  valueFormat: T_ChartValueFormat | undefined,
): string {
  return valueFormat === "currency" ? "$" : "";
}

function getDefaultChartValueSuffix(
  valueFormat: T_ChartValueFormat | undefined,
): string {
  switch (valueFormat) {
    case "percent":
      return "%";
    case "bps":
      return " bps";
    case "multiple":
      return "x";
    default:
      return "";
  }
}

export function formatChartDisplayValue(
  value: number,
  options: T_ChartValueDisplayOptions = {},
): string {
  const prefix =
    options.valuePrefix ?? getDefaultChartValuePrefix(options.valueFormat);
  const suffix =
    options.valueSuffix ?? getDefaultChartValueSuffix(options.valueFormat);

  return `${prefix}${formatChartValue(value)}${suffix}`;
}

/** Scrub-readout counterpart of formatChartDisplayValue. */
export function formatChartDetailDisplayValue(
  value: number,
  options: T_ChartValueDisplayOptions = {},
): string {
  const prefix =
    options.valuePrefix ?? getDefaultChartValuePrefix(options.valueFormat);
  const suffix =
    options.valueSuffix ?? getDefaultChartValueSuffix(options.valueFormat);

  return `${prefix}${formatChartDetailValue(value)}${suffix}`;
}

/**
 * Clean chart labels by removing common prefixes like "FY" (fiscal year)
 * Examples:
 * - "FY2023" -> "2023"
 * - "FY 2023" -> "2023"
 * - "FY23" -> "23"
 * - "2023" -> "2023" (unchanged)
 *
 * @param label - Label text
 * @returns Cleaned label
 */
export function cleanChartLabel(label: unknown): string {
  const trimmed = normalizeChartLabel(label).trim();

  // Match "FY" followed by optional space and a year (2 or 4 digits)
  const fyMatch = trimmed.match(/^FY\s*(\d{2,4})$/i);
  if (fyMatch) {
    const year = fyMatch[1];
    if (year === undefined) {
      throw new Error("Expected year capture group in FY match");
    }
    return year;
  }

  return trimmed;
}

/**
 * Check if chart data has multi-series
 * Re-export for convenience
 */
export { getSeriesNames, isMultiSeries } from "@lattice/core-lib/types/chart";
